import { useCallback, useMemo } from 'react';
import { FieldSection } from 'api/field-sections/types';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import type {
  RbacActionScope,
  RbacPermissionRule,
  RbacRequestScope,
} from 'api/rbac/rbac-types';
import { Field } from 'api/tasks';
import { useProjectDetail } from 'data/projects';
import { useAccountDataQuery } from 'queries/account';
import { usePermissionSetDefinitionsForCtxMap } from 'queries/rbac';

export type ProjectResource = ['Project', Project];
export const ProjectResource = (project: Project): ProjectResource => [
  'Project',
  project,
];
export const ProjectResourceOrUndefined = (
  project?: Project,
): ProjectResource | undefined =>
  project != null ? ProjectResource(project) : undefined;
type FieldResource = ['Field', Field];
export const FieldResource = (field: Field): FieldResource => ['Field', field];
type FieldSectionResource = ['FieldSection', FieldSection];
export const FieldSectionResource = (
  fieldSection: FieldSection,
): FieldSectionResource => ['FieldSection', fieldSection];
type FieldResourceInProject = ['FieldResourceInProject', Project, Field];
export const FieldResourceInProject = (
  project: Project,
  field: Field,
): FieldResourceInProject => ['FieldResourceInProject', project, field];

type Disabled = Record<string, unknown>;

export interface UseWithAccess {
  rule?: RbacPermissionRule;
  propsIfNoAccess?: Disabled;
  children?: React.ReactNode;
  resourceData?:
    | ProjectResource
    | FieldResource
    | FieldSectionResource
    | FieldResourceInProject;
  actionScope?: Array<RbacActionScope>;
}

const useHasAccessToProjectResource = () => {
  const { data: permissionSetDefinitionsMap } =
    usePermissionSetDefinitionsForCtxMap();

  const hasAccessToProjectResource = useCallback(
    (
      rule: RbacPermissionRule,
      project: Project,
      actionScope: Array<RbacActionScope> = [],
    ) => {
      if (rule == null) {
        return true;
      }

      const permissionSetId = project?.permissionSet?.permissionSetId;

      if (permissionSetId == null) {
        return false;
      }
      const permissionRule =
        permissionSetDefinitionsMap?.[permissionSetId]?.permissionMap?.[rule];

      if (permissionRule == null || permissionRule.requestScope === 'NONE') {
        return false;
      }

      // if no actionScope is passed, then the user has access to the resource
      if (!actionScope.length) {
        return true;
      }

      // check if passed actionScope is there in permissionRule.actionScope from server
      // actionScope should be the subset of permissionRule.actionScope
      if (
        actionScope.length &&
        actionScope.every((scope) =>
          permissionRule?.actionScopes?.includes(scope),
        )
      ) {
        return true;
      }

      return false;
    },
    [permissionSetDefinitionsMap],
  );

  const getRequestScopeForProjectResource = useCallback(
    ({ rule, resourceData }: UseWithAccess) => {
      if (resourceData == null) {
        return undefined;
      }

      const permissionSetId = (resourceData as ProjectResource)[1]
        ?.permissionSet?.permissionSetId;

      if (permissionSetId == null) {
        return undefined;
      }

      if (rule == null) {
        return undefined;
      }

      const permissionRule =
        permissionSetDefinitionsMap?.[permissionSetId]?.permissionMap?.[rule];

      return permissionRule?.requestScope;
    },
    [permissionSetDefinitionsMap],
  );

  return {
    hasAccessToProjectResource,
    getRequestScopeForProjectResource,
  };
};

const useHasAccessToFieldResourceInProject = () => {
  const { data: permissionSetDefinitionsMap } =
    usePermissionSetDefinitionsForCtxMap();

  const { data: accountData } = useAccountDataQuery();
  const userGroupIds = useMemo(
    () =>
      (accountData?.userProfile.user.userGroups ?? []).map(
        ({ userGroupsId }) => userGroupsId,
      ),
    [accountData?.userProfile.user.userGroups],
  );

  const hasHasAccessToFieldResourceInProject = useCallback(
    (rule: RbacPermissionRule, project: Project, field: Field) => {
      if (rule == null) {
        return true;
      }

      const permissionSetId = project?.permissionSet?.permissionSetId;

      if (permissionSetId == null) {
        return false;
      }
      const permissionRule =
        permissionSetDefinitionsMap?.[permissionSetId]?.permissionMap?.[rule];

      if (permissionRule == null || permissionRule.requestScope === 'NONE') {
        return false;
      }
      if (
        permissionRule.requestScope === 'GLOBAL' ||
        permissionRule.requestScope === 'GROUP' ||
        permissionRule.requestScope === 'LESS_RESTRICTED' ||
        permissionRule.requestScope === 'RESTRICTED'
      ) {
        return true;
      }
      if (permissionRule.requestScope === 'GROUP') {
        return userGroupIds.some((id) =>
          (field.userGroupIds ?? []).includes(id),
        );
      }

      return false;
    },
    [permissionSetDefinitionsMap, userGroupIds],
  );

  return {
    hasHasAccessToFieldResourceInProject,
  };
};

const useHasAccessToResourceAssociatedWithGroups = <
  T extends { userGroupIds: number[] },
>() => {
  const { data: permissionSetDefinitionsMap, isLoading } =
    usePermissionSetDefinitionsForCtxMap();

  const { data: accountData } = useAccountDataQuery();
  const permissionSetId =
    accountData?.userProfile?.permissions?.permissionSet?.permissionSetId;
  const userGroupIds = useMemo(
    () =>
      (accountData?.userProfile.user.userGroups ?? []).map(
        ({ userGroupsId }) => userGroupsId,
      ),
    [accountData?.userProfile.user.userGroups],
  );

  const hasAccessToResourceAssociatedWithGroups = useCallback(
    (rule: RbacPermissionRule, resource: T) => {
      if (rule == null) {
        return true;
      }

      if (permissionSetId == null) {
        return false;
      }
      const permissionRule =
        permissionSetDefinitionsMap?.[permissionSetId]?.permissionMap?.[rule];

      if (permissionRule == null || permissionRule.requestScope === 'NONE') {
        return false;
      }
      if (
        permissionRule.requestScope === 'GLOBAL' ||
        permissionRule.requestScope === 'LESS_RESTRICTED' ||
        permissionRule.requestScope === 'RESTRICTED'
      ) {
        return true;
      }
      if (permissionRule.requestScope === 'GROUP') {
        return userGroupIds.some((id) => resource.userGroupIds.includes(id));
      }

      return false;
    },
    [permissionSetDefinitionsMap, permissionSetId, userGroupIds],
  );

  return {
    hasAccessToResourceAssociatedWithGroups,
    isLoading,
  };
};

const useHasAccessToAccountResource = () => {
  const { data: permissionSetDefinitionsMap } =
    usePermissionSetDefinitionsForCtxMap();

  const { data: accountData } = useAccountDataQuery();
  const getRuleForResource = useCallback(
    (rule: RbacPermissionRule) => {
      const permissionSetId =
        accountData?.userProfile?.permissions?.permissionSet?.permissionSetId;

      if (permissionSetId == null) {
        return undefined;
      }

      const permissionRule =
        permissionSetDefinitionsMap?.[permissionSetId]?.permissionMap?.[rule];

      if (permissionRule == null) {
        const defaultProjectPermission =
          permissionSetDefinitionsMap?.[permissionSetId]
            ?.defaultProjectPermission;

        if (defaultProjectPermission != null) {
          return permissionSetDefinitionsMap?.[defaultProjectPermission]
            ?.permissionMap?.[rule];
        }
      }

      return permissionRule;
    },
    [
      accountData?.userProfile?.permissions?.permissionSet?.permissionSetId,
      permissionSetDefinitionsMap,
    ],
  );

  const hasAccessToAccountResource = useCallback(
    (rule: RbacPermissionRule, actionScope: Array<RbacActionScope> = []) => {
      if (rule == null) {
        return true;
      }
      const permissionSetId =
        accountData?.userProfile?.permissions?.permissionSet?.permissionSetId;

      if (permissionSetId == null) {
        return false;
      }

      const permissionRule = getRuleForResource(rule);

      if (permissionRule == null) {
        return false;
      }

      // check if passed actionScope is there in permissionRule.actionScope from server
      // if the actionscope itself is not passed, then it is assumed that the user has access to the resource
      if (
        actionScope.length === 0 ||
        (actionScope &&
          actionScope.every((scope) =>
            permissionRule?.actionScopes?.includes(scope),
          ))
      ) {
        return true;
      }

      return false;
    },
    [
      accountData?.userProfile?.permissions?.permissionSet?.permissionSetId,
      getRuleForResource,
    ],
  );

  return {
    hasAccessToAccountResource,
  };
};

export const useWithAccess = (): {
  hasAccessToResource: (args: UseWithAccess) => boolean;
  getRequestScopeForProjectResource: (
    args: UseWithAccess,
  ) => RbacRequestScope | undefined;
  isLoading: boolean;
} => {
  const { hasAccessToAccountResource } = useHasAccessToAccountResource();
  const { hasAccessToProjectResource, getRequestScopeForProjectResource } =
    useHasAccessToProjectResource();
  const { hasAccessToResourceAssociatedWithGroups, isLoading } =
    useHasAccessToResourceAssociatedWithGroups();
  const { hasHasAccessToFieldResourceInProject } =
    useHasAccessToFieldResourceInProject();

  const hasAccessToResource = useCallback(
    ({ rule, resourceData, actionScope }: UseWithAccess) => {
      // if rule is not specified, then the user has access to the resource
      if (!rule) {
        return true;
      }
      if (resourceData == null) {
        return hasAccessToAccountResource(rule, actionScope);
      }
      const resourceName = resourceData?.[0];

      switch (resourceName) {
        case 'Project':
          return hasAccessToProjectResource(rule, resourceData[1], actionScope);
        case 'Field':
          return hasAccessToResourceAssociatedWithGroups(rule, resourceData[1]);
        case 'FieldResourceInProject':
          return hasHasAccessToFieldResourceInProject(
            rule,
            resourceData[1],
            resourceData[2],
          );
        default:
          return hasAccessToAccountResource(rule, actionScope);
      }
    },
    [
      hasAccessToAccountResource,
      hasAccessToProjectResource,
      hasAccessToResourceAssociatedWithGroups,
      hasHasAccessToFieldResourceInProject,
    ],
  );

  return { hasAccessToResource, getRequestScopeForProjectResource, isLoading };
};

export const useProjectAsRBACResource = (
  projectId: number | undefined,
): ProjectResource | undefined => {
  const { data } = useProjectDetail(ProjectId(projectId!));

  return useMemo(() => ProjectResourceOrUndefined(data), [data]);
};
